import { API, Auth } from 'aws-amplify';
import { executeWithRetry, executeWithRetryAndDedupe } from './Base';
import { apiClient } from './AmplifyWrapper';

const useAmplify = false;
const API_NAME = 'CoreServicesAPI';

async function getRequestHeaders (headers) {
  const currentSession = await Auth.currentSession();
  const header = {
    headers: Object.assign({}, {
      // Activate following headers when connecting to a local instance of BuildsimpleCore
      // 'x-caller-id': 'local:CognitoIdentityCredentials',
      // 'cognito-authentication-type': 'authenticated',
      // 'x-iam-user': 'local:CognitoIdentityCredentials',
      // 'x-cognito-type': 'authenticated',
      // 'x-cognito-id': 'id',
      'Bs-Token': 'Bearer ' + currentSession.idToken.jwtToken
    }, headers)
  };
  return header;
}

export async function loadSiemConfig (tenantId) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  const id = `${API_NAME}.loadSiemConfig()`;
  if (useAmplify) {
    return await executeWithRetry(async () => await API.get(API_NAME, '/siem-config', params), 5);
  } else {
    return await executeWithRetryAndDedupe((signal) => apiClient.get(API_NAME, '/siem-config', { ...params, signal }), id);
  }
}

export async function postSiemConfig (tenantId, type, config) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  params.body = {
    type,
    ...config
  };
  const id = `${API_NAME}.postSiemConfig(${type},${config})`;
  if (useAmplify) {
    return await executeWithRetry(async () => await API.post(API_NAME, '/siem-config', params), 5);
  } else {
    return await executeWithRetryAndDedupe((signal) => apiClient.post(API_NAME, '/siem-config', { ...params, signal }), id);
  }
}

export async function putSiemConfig (tenantId, type, config) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  params.body = {
    type,
    ...config
  };
  const id = `${API_NAME}.putSiemConfig(${type},${config})`;
  if (useAmplify) {
    return await executeWithRetry(async () => await API.put(API_NAME, '/siem-config', params), 5);
  } else {
    return await executeWithRetryAndDedupe((signal) => apiClient.put(API_NAME, '/siem-config', { ...params, signal }), id);
  }
}

export async function deleteSiemConfig (tenantId) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  const id = `${API_NAME}.deleteSiemConfig()`;
  if (useAmplify) {
    return await executeWithRetry(async () => await API.del(API_NAME, '/siem-config', params), 5);
  } else {
    return await executeWithRetryAndDedupe((signal) => apiClient.delete(API_NAME, '/siem-config', { ...params, signal }), id);
  }
}

// The event queries may need several DynamoDB requests on the server, so allow more time than the default 8s.
const SIEM_EVENTS_REQUEST_PARAMS = { timeout: 20000 };

function toIsoString (value) {
  if (!value) return undefined;
  const date = value instanceof Date ? value : new Date(value);
  return isNaN(date) ? undefined : date.toISOString();
}

/**
 * Builds the query of GET /siem-events. Empty values are omitted, multiple actions are sent comma separated
 * (Spring splits them into a Set).
 */
export function createSiemEventsQuery ({ from, to, actions, userId, resourceType, status, q, limit, cursor } = {}) {
  const query = {
    from: toIsoString(from),
    to: toIsoString(to),
    action: actions?.length ? actions.join(',') : undefined,
    userId,
    resourceType,
    status,
    q: q?.trim(),
    limit,
    cursor
  };
  return Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined && value !== null && value !== ''));
}

/**
 * @returns {Promise<{items: Array, nextCursor?: string, truncated: boolean}>}
 */
export async function loadSiemEvents (tenantId, filter) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  params.queryStringParameters = createSiemEventsQuery(filter);
  const id = `${API_NAME}.loadSiemEvents(${JSON.stringify(params.queryStringParameters)})`;
  return await executeWithRetryAndDedupe((signal) => apiClient.get(API_NAME, '/siem-events', { ...params, signal }), id, SIEM_EVENTS_REQUEST_PARAMS);
}

export async function loadSiemEvent (tenantId, timestampAndId) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  // The sort key contains a '#', which must be encoded to be part of the path.
  const path = `/siem-events/${encodeURIComponent(timestampAndId)}`;
  const id = `${API_NAME}.loadSiemEvent(${timestampAndId})`;
  return await executeWithRetryAndDedupe((signal) => apiClient.get(API_NAME, path, { ...params, signal }), id);
}

export async function loadSiemEventFilterOptions (tenantId) {
  const headers = tenantId ? { 'X-Tenant-Id': tenantId } : {};
  const params = await getRequestHeaders(headers);
  const id = `${API_NAME}.loadSiemEventFilterOptions()`;
  return await executeWithRetryAndDedupe((signal) => apiClient.get(API_NAME, '/siem-events/filter-options', { ...params, signal }), id);
}
